import type { HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import pMap from "p-map";

import type { AppDispatch, AppStore, RootState } from "app/store";
import { hiddenActions } from "contexts/hidden";
import type { DispatchHidden } from "contexts/hidden/reducer";
import { HighlightCollection, highlightCollectionsActions } from "contexts/highlightCollections";
import type { DispatchHighlightCollection } from "contexts/highlightCollections/reducer";
import { highlightActions } from "contexts/highlighted";
import type { DispatchHighlighted } from "contexts/highlighted/reducer";
import { selectionBasketActions } from "contexts/selectionBasket";
import type { DispatchSelectionBasket } from "contexts/selectionBasket/reducer";
import type { ObjectDataAccess, PropertyQuery } from "dataAccess";
import {
    propertiesActions,
    type PropertyFilterSnapshot,
    type SelectedProperty,
    selectSelectedProperties,
} from "features/properties/slice";
import {
    renderActions,
    selectDefaultVisibility,
    selectMainObjects,
    selectPlainIsolationActive,
    selectPlainIsolationPaused,
    selectPlainIsolationSnapshotIds,
    selectPreIsolationVisibility,
} from "features/render";
import { ObjectVisibility } from "features/render/types";
import { undoRedoActions } from "features/undoRedo";
import { telemetry } from "telemetry";
import type { DeepMutable } from "types/misc";

import { getFilePathFromObjectPath, isLeafNode } from "./objectData";
import { SYNTHETIC_NODE_ID } from "./objectPath";
import { type PerfMeasurement, startMeasure } from "./performanceMeasure";
import { type IdList, SelectionSet } from "./selectionSet";
import { restoreUndoIds, snapshotUndoIds } from "./undoIds";

/**
 * One condition on one property. Values are ORed, criteria are ANDed, and matching is
 * exact — the widest thing {@link ObjectDataAccess.findByProperties} answers the same
 * way on every backend. Every caller already asked for exact matching, so there is no
 * flag.
 */
export interface SearchCriteria {
    property: string;
    /** Any one of these values matches, compared exactly. */
    value: string[];
}

/**
 * Objects matching every criterion, plus everything beneath the ones that are not
 * leaves: a container that matches is a match for its whole subtree, which is what
 * "similar objects" means to someone hiding or isolating them.
 *
 * Only the criteria-to-query mapping lives here — {@link SearchCriteria} is a UI shape.
 * How the match set and its subtrees are read differs per backend, so
 * {@link ObjectDataAccess.findIdsByProperties} owns that.
 */
export function findSimilarObjects({
    dataAccess,
    searchCriteria,
    signal,
}: {
    dataAccess: ObjectDataAccess;
    searchCriteria: SearchCriteria[];
    signal?: AbortSignal;
}): Promise<Uint32Array> {
    const query: PropertyQuery = {
        terms: searchCriteria.map(({ property, value }) => ({ property, anyOf: value })),
    };

    return dataAccess.findIdsByProperties(query, { includeDescendants: true, signal });
}

/**
 * Get default search criteria for an object based on its file type and properties.
 * This replicates the original logic from procoreStamp but returns the criteria
 * instead of directly searching.
 */
export function getDefaultSearchCriteria(obj: ObjectData): SearchCriteria[] {
    if (!obj) {
        console.warn("getDefaultSearchCriteria called with null/undefined object");
        return [];
    }

    if (!obj.path || !obj.name) {
        console.warn("Object missing required path or name:", { path: obj.path, name: obj.name });
        return [];
    }

    const filePath = getFilePathFromObjectPath(obj.path)?.toLowerCase();

    if (filePath) {
        if (filePath.endsWith(".ifc")) {
            const ifcClass = obj.properties?.find((p) => p[0].toLowerCase() === "ifcclass")?.[1];
            if (ifcClass) {
                return [{ property: "ifcClass", value: [ifcClass] }];
            }
        } else if (filePath.endsWith(".dwg")) {
            const layer = obj.properties?.find((p) => p[0].toLowerCase() === "dwg/layer")?.[1];
            if (layer) {
                return [{ property: "dwg/layer", value: [layer] }];
            }
        }
    }

    // Fallback to name
    return [{ property: "name", value: [obj.name] }];
}

function appendId(ids: IdList, extra: number): IdList {
    if (ids instanceof Uint32Array) {
        const out = new Uint32Array(ids.length + 1);
        out.set(ids);
        out[ids.length] = extra;
        out.sort();
        return out;
    }
    const out = ids.slice();
    out.push(extra);
    return out;
}

export function hideObjects(
    objectIds: IdList,
    {
        dispatch,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        dispatchHighlightCollections,
        currentSecondaryHighlightIds,
        currentMainObject,
        currentMainObjects = [],
        currentHiddenIds,
        currentLastHiddenIds,
        preserveSelection = false,
        includeMainObjectToHide = false,
    }: {
        dispatch: AppDispatch;
        dispatchHighlighted: DispatchHighlighted;
        dispatchHidden: DispatchHidden;
        dispatchSelectionBasket: DispatchSelectionBasket;
        dispatchHighlightCollections?: DispatchHighlightCollection; // Not used in Procore
        currentSecondaryHighlightIds?: IdList;
        currentMainObject?: number;
        currentMainObjects?: number[];
        currentHiddenIds: SelectionSet;
        currentLastHiddenIds: Iterable<number>;
        preserveSelection?: boolean;
        // Appends the current main object to the hide set when enabled.
        // Does not affect selection-clearing, which is controlled by preserveSelection.
        includeMainObjectToHide?: boolean;
    },
): void {
    // Most of the time highlights include main object but not always.
    // Keep this as an opt-in to preserve strict hide semantics by default.
    const objectIdsWithMain =
        includeMainObjectToHide && currentMainObject !== undefined && !objectIds.includes(currentMainObject)
            ? appendId(objectIds, currentMainObject)
            : objectIds;
    const newlyHiddenIds = objectIdsWithMain.filter((id: number) => !currentHiddenIds.has(id));

    if (newlyHiddenIds.length === 0) {
        return;
    }

    const hide = (toHide: Iterable<ObjectId>, withMain: Iterable<ObjectId>) => {
        if (!preserveSelection) {
            dispatch(renderActions.setMainObject(undefined));
            dispatch(renderActions.clearMainObjects());
            dispatchHighlighted(highlightActions.remove(withMain));
            dispatchSelectionBasket(selectionBasketActions.remove(withMain));
            dispatchHighlightCollections?.(
                highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, []),
            );
        }

        dispatchHidden(hiddenActions.add(toHide));
        dispatchHidden(hiddenActions.setLastHiddenIds(toHide));
    };
    hide(newlyHiddenIds, objectIdsWithMain);

    const shrunkNewlyHidden = snapshotUndoIds(newlyHiddenIds);
    const shrunkLastHidden = snapshotUndoIds(currentLastHiddenIds);
    const shrunkWithMain = snapshotUndoIds(objectIdsWithMain);
    const shrunkSecondary = currentSecondaryHighlightIds ? snapshotUndoIds(currentSecondaryHighlightIds) : undefined;

    dispatch(
        undoRedoActions.push({
            description: `Hide ${newlyHiddenIds.length} object(s)`,
            undoCallback: () => {
                if (!preserveSelection) {
                    dispatch(renderActions.setMainObject(currentMainObject));
                    dispatch(renderActions.setMainObjects(currentMainObjects));
                    dispatchHighlighted(highlightActions.add(restoreUndoIds(shrunkWithMain)));
                    dispatchSelectionBasket(selectionBasketActions.add(restoreUndoIds(shrunkWithMain)));
                    if (dispatchHighlightCollections && shrunkSecondary) {
                        dispatchHighlightCollections(
                            highlightCollectionsActions.setIds(
                                HighlightCollection.SecondaryHighlight,
                                restoreUndoIds(shrunkSecondary),
                            ),
                        );
                    }
                }
                dispatchHidden(hiddenActions.remove(restoreUndoIds(shrunkNewlyHidden)));
                dispatchHidden(hiddenActions.setLastHiddenIds(restoreUndoIds(shrunkLastHidden)));
            },
            redoCallback: () => hide(restoreUndoIds(shrunkNewlyHidden), restoreUndoIds(shrunkWithMain)),
        }),
    );
}

export function showObjects(
    objectIds: IdList,
    {
        dispatch,
        dispatchHidden,
    }: {
        dispatch: AppDispatch;
        dispatchHidden: DispatchHidden;
    },
): void {
    const show = () => {
        dispatchHidden(hiddenActions.remove(objectIds));
    };
    show();

    const shrunkIds = snapshotUndoIds(objectIds);

    dispatch(
        undoRedoActions.push({
            description: `Show ${objectIds.length} object(s)`,
            undoCallback: () => {
                dispatchHidden(hiddenActions.add(restoreUndoIds(shrunkIds)));
            },
            redoCallback: () => {
                dispatchHidden(hiddenActions.remove(restoreUndoIds(shrunkIds)));
            },
        }),
    );
}

export async function hideSimilarObjects(
    obj: ObjectData,
    {
        dataAccess,
        dispatch,
        dispatchHidden,
        currentHiddenIds,
        currentLastHiddenIds,
    }: {
        dataAccess: ObjectDataAccess;
        dispatch: AppDispatch;
        dispatchHidden: DispatchHidden;
        currentHiddenIds: IdList;
        currentLastHiddenIds: Iterable<number>;
    },
    searchCriteria?: SearchCriteria[],
): Promise<void> {
    const criteria = searchCriteria ?? getDefaultSearchCriteria(obj);

    if (criteria.length === 0) {
        console.warn("No valid search criteria for hideSimilarObjects");
        return;
    }

    const objectIds = await findSimilarObjects({ dataAccess, searchCriteria: criteria });

    const execute = () => {
        dispatchHidden(hiddenActions.add(objectIds));
        dispatchHidden(hiddenActions.setLastHiddenIds(objectIds));
    };
    execute();

    const shrunkSimilar = snapshotUndoIds(objectIds);
    const shrunkHidden = snapshotUndoIds(currentHiddenIds);
    const shrunkLastHidden = snapshotUndoIds(currentLastHiddenIds);

    dispatch(
        undoRedoActions.push({
            description: `Hide ${objectIds.length} similar object(s)`,
            undoCallback: () => {
                dispatchHidden(hiddenActions.setIds(restoreUndoIds(shrunkHidden)));
                dispatchHidden(hiddenActions.setLastHiddenIds(restoreUndoIds(shrunkLastHidden)));
            },
            redoCallback: () => {
                const ids = restoreUndoIds(shrunkSimilar);
                dispatchHidden(hiddenActions.add(ids));
                dispatchHidden(hiddenActions.setLastHiddenIds(ids));
            },
        }),
    );
}

export function unhideLastHiddenObjects(
    lastHiddenIds: Iterable<number>,
    {
        dispatch,
        dispatchHidden,
    }: {
        dispatch: AppDispatch;
        dispatchHidden: DispatchHidden;
    },
): void {
    const lastHidden = lastHiddenIds instanceof SelectionSet ? lastHiddenIds : SelectionSet.fromIds(lastHiddenIds);
    if (lastHidden.size === 0) {
        return;
    }

    const idsToUnhide =
        Array.isArray(lastHiddenIds) || lastHiddenIds instanceof Uint32Array ? lastHiddenIds : lastHidden;

    const unhide = () => {
        dispatchHidden(hiddenActions.remove(idsToUnhide));
        dispatchHidden(hiddenActions.setLastHiddenIds([]));
    };
    unhide();

    const shrunkLastHidden = snapshotUndoIds(lastHidden);

    dispatch(
        undoRedoActions.push({
            description: `Unhide last hidden objects`,
            undoCallback: () => {
                const ids = restoreUndoIds(shrunkLastHidden);
                dispatchHidden(hiddenActions.add(ids));
                dispatchHidden(hiddenActions.setLastHiddenIds(ids));
            },
            redoCallback: () => {
                dispatchHidden(hiddenActions.remove(restoreUndoIds(shrunkLastHidden)));
                dispatchHidden(hiddenActions.setLastHiddenIds([]));
            },
        }),
    );
}

export function unselectAll({
    dispatch,
    dispatchHighlighted,
    dispatchHighlightCollections,
    dispatchSelectionBasket,
    currentHighlightedIds,
    currentMainObject,
    currentMainObjects = [],
    currentSelectionBasketIds,
}: {
    dispatch: AppDispatch;
    dispatchHighlighted: DispatchHighlighted;
    dispatchHighlightCollections?: DispatchHighlightCollection;
    dispatchSelectionBasket?: DispatchSelectionBasket;
    currentHighlightedIds: IdList;
    currentMainObject: number | undefined;
    currentMainObjects?: number[];
    currentSelectionBasketIds?: Iterable<number>;
}): void {
    const unselect = () => {
        dispatchHighlighted(highlightActions.setIds([]));
        dispatchHighlightCollections?.(highlightCollectionsActions.clearAll());
        dispatchSelectionBasket?.(selectionBasketActions.set([]));
        dispatch(renderActions.setMainObject(undefined));
        dispatch(renderActions.clearMainObjects());
    };
    unselect();

    const shrunkHighlighted = snapshotUndoIds(currentHighlightedIds);
    const shrunkBasket = currentSelectionBasketIds ? snapshotUndoIds(currentSelectionBasketIds) : undefined;

    dispatch(
        undoRedoActions.push({
            description: "Unselect objects",
            undoCallback: () => {
                dispatchHighlighted(highlightActions.setIds(restoreUndoIds(shrunkHighlighted)));
                dispatch(renderActions.setMainObject(currentMainObject));
                dispatch(renderActions.setMainObjects(currentMainObjects));
                if (dispatchSelectionBasket && shrunkBasket) {
                    dispatchSelectionBasket(selectionBasketActions.set(restoreUndoIds(shrunkBasket)));
                }
            },
            redoCallback: unselect,
        }),
    );
}

function selectObjectsRaw(
    objectIds: Iterable<ObjectId>,
    { dispatchHighlighted }: { dispatchHighlighted: DispatchHighlighted },
): void {
    dispatchHighlighted(highlightActions.setIds(objectIds));
}

export function selectObjects(
    objectIds: number[],
    {
        dispatch,
        dispatchHighlighted,
        mainObject,
        currentHighlightedIds,
        currentMainObject,
    }: {
        dispatch: AppDispatch;
        dispatchHighlighted: DispatchHighlighted;
        mainObject: number | undefined;
        currentHighlightedIds: IdList;
        currentMainObject: number | undefined;
    },
): void {
    const select = () => {
        dispatchHighlighted(highlightActions.setIds(objectIds));
        dispatch(renderActions.setMainObject(mainObject));
    };
    select();

    const shrunkNext = snapshotUndoIds(objectIds);
    const shrunkPrev = snapshotUndoIds(currentHighlightedIds);

    dispatch(
        undoRedoActions.push({
            description: `Select ${objectIds.length} object(s)`,
            undoCallback: () => {
                dispatchHighlighted(highlightActions.setIds(restoreUndoIds(shrunkPrev)));
                dispatch(renderActions.setMainObject(currentMainObject));
            },
            redoCallback: () => {
                dispatchHighlighted(highlightActions.setIds(restoreUndoIds(shrunkNext)));
                dispatch(renderActions.setMainObject(mainObject));
            },
        }),
    );
}

/**
 * Select all objects with properties similar to `obj`.
 *
 * Per the selection-resolution spec, Select Similar has **no knowledge of object
 * resolution**: the matched nodes ARE the selection. Resolution only decides which node a
 * 3D click becomes; it does not follow the selection into similar/hide-similar.
 *
 * `mainObjects` receives the match IDs (the nodes whose properties matched).
 * `highlighted` receives the matches plus their descendant geometry (`includeDescendants`),
 * so the viewer paints the full subtree of each match.
 *
 * @see https://procoretech.atlassian.net/wiki/x/AYAQZgE
 */
export async function selectSimilarObjects(
    obj: ObjectData,
    {
        dataAccess,
        dispatch,
        dispatchHighlighted,
        currentHighlightedIds,
        currentMainObject,
        currentMainObjects = [],
    }: {
        dataAccess: ObjectDataAccess;
        dispatch: AppDispatch;
        dispatchHighlighted: DispatchHighlighted;
        currentHighlightedIds: IdList;
        /** The current mainObject (singular) — restored on undo. */
        currentMainObject?: number;
        /** The current mainObjects array — restored on undo. */
        currentMainObjects?: number[];
    },
    searchCriteria?: SearchCriteria[],
): Promise<void> {
    const criteria = searchCriteria ?? getDefaultSearchCriteria(obj);

    if (criteria.length === 0) {
        console.warn("No valid search criteria for selectSimilarObjects");
        return;
    }

    const query: PropertyQuery = { terms: criteria.map(({ property, value }) => ({ property, anyOf: value })) };

    // Two reads of the same query:
    // - paintedIds (includeDescendants): matches + everything beneath them — what the viewer highlights.
    // - matchIds (no descendants): only the matched nodes — what goes into mainObjects for the tree.
    const [paintedIds, matchIds] = await Promise.all([
        dataAccess.findIdsByProperties(query, { includeDescendants: true }),
        dataAccess.findIdsByProperties(query),
    ]);

    const mainObjectIds = Array.from(matchIds);
    const redoMainObject = mainObjectIds[0];

    selectObjectsRaw(paintedIds, { dispatchHighlighted });
    dispatch(renderActions.setMainObject(redoMainObject));
    dispatch(renderActions.setMainObjects(mainObjectIds));

    const shrunkAll = snapshotUndoIds(paintedIds);
    const shrunkMainObjects = snapshotUndoIds(mainObjectIds);
    const shrunkPrev = snapshotUndoIds(currentHighlightedIds);

    dispatch(
        undoRedoActions.push({
            description: `Select ${mainObjectIds.length} similar object(s)`,
            undoCallback: () => {
                selectObjectsRaw(restoreUndoIds(shrunkPrev), { dispatchHighlighted });
                dispatch(renderActions.setMainObject(currentMainObject));
                dispatch(renderActions.setMainObjects(currentMainObjects));
            },
            redoCallback: () => {
                selectObjectsRaw(restoreUndoIds(shrunkAll), { dispatchHighlighted });
                dispatch(renderActions.setMainObject(redoMainObject));
                dispatch(renderActions.setMainObjects(Array.from(restoreUndoIds(shrunkMainObjects).sortedIds)));
            },
        }),
    );
}

/** Visibility state as it was before an isolate, so the action's undo can put it back. */
export type VisibilitySnapshot = {
    defaultVisibility: ObjectVisibility;
    preIsolationVisibility: ObjectVisibility | undefined;
    /**
     * Plain Isolate's keep-set. Every visibility action drops it, so undo has to re-enter
     * Isolate mode rather than only restoring the Transparent it renders as.
     */
    plainIsolation: { active: boolean; snapshotIds: number[] | undefined };
    /** Whether Isolate was paused (`pausePlainIsolation`) rather than off, so undo can resume it. */
    plainIsolationPaused: boolean;
};

/**
 * Read a {@link VisibilitySnapshot} off the store for an action's undo to restore. Call
 * it as the action runs, not at render: the snapshot has to be what the user is undoing.
 */
export function captureVisibilitySnapshot(state: RootState): VisibilitySnapshot {
    return {
        defaultVisibility: selectDefaultVisibility(state),
        preIsolationVisibility: selectPreIsolationVisibility(state),
        plainIsolation: {
            active: selectPlainIsolationActive(state),
            snapshotIds: selectPlainIsolationSnapshotIds(state),
        },
        plainIsolationPaused: selectPlainIsolationPaused(state),
    };
}

/** Enter X-ray isolation. No undo entry — callers that need one push their own. */
function applyXRayIsolation({
    dispatch,
    currentDefaultVisibility,
}: {
    dispatch: AppDispatch;
    currentDefaultVisibility: ObjectVisibility;
}): void {
    dispatch(renderActions.setPreIsolationVisibility(currentDefaultVisibility));
    dispatch(renderActions.setVisibilityMode(ObjectVisibility.Xray));
}

/**
 * Put back a {@link VisibilitySnapshot}.
 *
 * `setVisibilityMode` rather than `setDefaultVisibility` so restoring X-ray also re-derives
 * the pick opacity threshold, and `preIsolationVisibility` after it, since the mode drops
 * that for any non-isolated visibility.
 */
export function restoreVisibility({
    dispatch,
    snapshot,
}: {
    dispatch: AppDispatch;
    snapshot: VisibilitySnapshot;
}): void {
    dispatch(renderActions.setVisibilityMode(snapshot.defaultVisibility));

    if (snapshot.preIsolationVisibility !== undefined) {
        dispatch(renderActions.setPreIsolationVisibility(snapshot.preIsolationVisibility));
    } else {
        dispatch(renderActions.clearPreIsolationVisibility());
    }

    // Last, because both dispatches above clear plain isolation on their way through.
    if (snapshot.plainIsolationPaused) {
        dispatch(renderActions.pausePlainIsolation());
        if (snapshot.plainIsolation.snapshotIds !== undefined) {
            dispatch(renderActions.setPlainIsolationSnapshotIds(snapshot.plainIsolation.snapshotIds));
        }
    } else {
        dispatch(renderActions.setPlainIsolationActive(snapshot.plainIsolation));
    }
}

export function isolateObjects({
    dispatch,
    isolationIds,
    currentDefaultVisibility,
    visibilityBefore,
    propertyFilter,
}: {
    dispatch: AppDispatch;
    isolationIds: IdList;
    /** Stored as `preIsolationVisibility`, so Unisolate returns here. */
    currentDefaultVisibility: ObjectVisibility;
    /**
     * Visibility as it actually was when Isolate ran, for undo. Distinct from
     * {@link currentDefaultVisibility}, which carries the *original* pre-isolation
     * visibility forward through chained isolations — isolating while already isolated in
     * X-ray makes the two differ, and undo has to land back in X-ray.
     */
    visibilityBefore: VisibilitySnapshot;
    propertyFilter: PropertyFilterSnapshot;
}): void {
    // Copy now so Isolate redo uses this keep-set, not the live selection.
    const snapshotIds = Array.from(isolationIds);
    const execute = () => {
        dispatch(renderActions.setPreIsolationVisibility(currentDefaultVisibility));
        dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Transparent));
        dispatch(
            renderActions.setPlainIsolationActive({
                active: true,
                snapshotIds,
            }),
        );
        dispatch(propertiesActions.clearPropertySelection());
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: "Isolate objects",
            undoCallback: () => {
                restoreVisibility({ dispatch, snapshot: visibilityBefore });
                dispatch(propertiesActions.setPropertyFilter(propertyFilter));
            },
            redoCallback: execute,
        }),
    );
}

export function unisolateObjects({
    dispatch,
    isolationIds,
    restoreToVisibility,
}: {
    dispatch: AppDispatch;
    /** Keep-set at Unisolate time. Undo re-applies this list, not later Isolates. */
    isolationIds: IdList;
    /** The visibility to restore to — typically the value saved as preIsolationVisibility. */
    restoreToVisibility: ObjectVisibility;
}): void {
    const snapshotIds = Array.from(isolationIds);
    const execute = () => {
        dispatch(renderActions.setPlainIsolationActive({ active: false }));
        dispatch(renderActions.clearPreIsolationVisibility());
        dispatch(renderActions.setDefaultVisibility(restoreToVisibility));
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: "Unisolate objects",
            undoCallback: () => {
                dispatch(renderActions.setPreIsolationVisibility(restoreToVisibility));
                dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Transparent));
                dispatch(
                    renderActions.setPlainIsolationActive({
                        active: true,
                        snapshotIds,
                    }),
                );
            },
            redoCallback: execute,
        }),
    );
}

export function isolateObjectsInXRayMode({
    dispatch,
    currentDefaultVisibility,
    propertyFilter,
}: {
    dispatch: AppDispatch;
    currentDefaultVisibility: ObjectVisibility;
    propertyFilter: PropertyFilterSnapshot;
}): void {
    // Entering X-ray leaves the checkboxes alone — that is the transition a check makes —
    // but undo leaves X-ray and clears them, so redo has to bring them back.
    const execute = () => {
        applyXRayIsolation({ dispatch, currentDefaultVisibility });
        dispatch(propertiesActions.setPropertyFilter(propertyFilter));
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: "Isolate objects in X-ray mode",
            undoCallback: () => {
                dispatch(renderActions.clearPreIsolationVisibility());
                dispatch(renderActions.setVisibilityMode(currentDefaultVisibility));
            },
            redoCallback: execute,
        }),
    );
}

export function unisolateObjectsFromXRayMode({
    dispatch,
    restoreToVisibility,
    propertyFilter,
}: {
    dispatch: AppDispatch;
    /** The visibility to restore to — typically the value saved as preIsolationVisibility. */
    restoreToVisibility: ObjectVisibility;
    propertyFilter: PropertyFilterSnapshot;
}): void {
    const execute = () => {
        dispatch(renderActions.clearPreIsolationVisibility());
        dispatch(renderActions.setVisibilityMode(restoreToVisibility));
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: "Unisolate objects from X-ray mode",
            undoCallback: () => {
                dispatch(renderActions.setPreIsolationVisibility(restoreToVisibility));
                dispatch(renderActions.setVisibilityMode(ObjectVisibility.Xray));
                dispatch(propertiesActions.setPropertyFilter(propertyFilter));
            },
            redoCallback: execute,
        }),
    );
}

export function exitXRayMode({ dispatch }: { dispatch: AppDispatch }): void {
    dispatch(renderActions.setVisibilityMode(ObjectVisibility.Neutral));
}

export function addLoadingHandle({ dispatch }: { dispatch: AppDispatch }): number {
    const handle = performance.now();
    dispatch(renderActions.addLoadingHandle(handle));
    return handle;
}

export function removeLoadingHandle(handle: number, { dispatch }: { dispatch: AppDispatch }): void {
    dispatch(renderActions.removeLoadingHandle(handle));
}

export async function withLoadingState<T>(
    operation: () => Promise<T>,
    { dispatch }: { dispatch: AppDispatch },
): Promise<T> {
    const handle = addLoadingHandle({ dispatch });
    try {
        return await operation();
    } finally {
        removeLoadingHandle(handle, { dispatch });
    }
}

/**
 * Apply the Properties-window checkbox selection: select every object sharing the checked
 * property values and isolate them in X-ray, or leave X-ray when nothing is checked.
 *
 * @param selectedProperties The selection *after* the toggle, already in the store.
 * @param options.before State from before the toggle. One undo entry covers the whole
 *   thing — the checkboxes, the highlight the search produced, the semantic selection,
 *   and the visibility — since undoing any of them alone would leave the others
 *   describing a filter that is gone.
 * @param options.signal Aborted by the caller when a newer toggle supersedes this one, so
 *   the search doesn't keep running just to have its result thrown away.
 */
export async function isolateSimilarInXray(
    selectedProperties: Record<string, SelectedProperty>,
    {
        dataAccess,
        store,
        dispatchHighlighted,
        signal,
        before,
    }: {
        dataAccess: ObjectDataAccess;
        store: AppStore;
        dispatchHighlighted: DispatchHighlighted;
        signal?: AbortSignal;
        before: {
            propertyFilter: PropertyFilterSnapshot;
            highlightedIds: IdList;
            visibility: VisibilitySnapshot;
        };
    },
): Promise<void> {
    const { dispatch } = store;
    // Isolating restores here, so a filter started while already isolated in X-ray (a
    // second checkbox flow chained onto the first) has to carry the *original*
    // pre-isolation visibility forward rather than re-capturing the current (X-ray) one.
    const currentDefaultVisibility = before.visibility.preIsolationVisibility ?? before.visibility.defaultVisibility;

    // Semantic selection is in Redux; read it here so undo cannot close over a stale hook value.
    const previousMainObjects = selectMainObjects(store.getState());
    const shrunkPrevHighlight = snapshotUndoIds(before.highlightedIds);
    const shrunkPrevMainObjects = snapshotUndoIds(previousMainObjects);
    const restoredMainObjects = () => Array.from(restoreUndoIds(shrunkPrevMainObjects).sortedIds);

    const undo = () => {
        selectObjectsRaw(restoreUndoIds(shrunkPrevHighlight), { dispatchHighlighted });
        restoreVisibility({ dispatch, snapshot: before.visibility });
        // After the visibility, which clears the checkboxes on its way through.
        dispatch(propertiesActions.setPropertyFilter(before.propertyFilter));
        dispatch(renderActions.setMainObjects(restoredMainObjects()));
    };

    const wasFiltering = Object.keys(before.propertyFilter.selectedProperties).length > 0;

    // Nothing checked: end the filter and hand back the selection it replaced.
    if (Object.keys(selectedProperties).length === 0) {
        const restoredHighlight = before.propertyFilter.preFilterSelection ?? [];
        const restoredSemantic = before.propertyFilter.preFilterMainObjects ?? [];
        const execute = () => {
            // Before leaving X-ray, which is what strands a selection dispatched after it.
            selectObjectsRaw(restoredHighlight, { dispatchHighlighted });
            dispatch(renderActions.setMainObjects(restoredSemantic));
            exitXRayMode({ dispatch });
        };
        execute();

        dispatch(
            undoRedoActions.push({
                description: "Clear property filter",
                undoCallback: undo,
                redoCallback: execute,
            }),
        );
        return;
    }

    /**
     * The first check is what starts a filter, so it records the selection it is about to
     * replace; later checks carry that record forward untouched.
     */
    const preFilterSelection = wasFiltering
        ? before.propertyFilter.preFilterSelection
        : Array.from(before.highlightedIds);
    const preFilterMainObjects = wasFiltering ? before.propertyFilter.preFilterMainObjects : previousMainObjects;

    const filterSnapshot: PropertyFilterSnapshot = {
        selectedProperties,
        preFilterSelection,
        preFilterMainObjects,
    };

    if (!wasFiltering) {
        // Commit synchronously — a toggle that starts before this search resolves reads
        // `before.propertyFilter` off the store, and needs preFilterSelection there
        // already rather than racing this search for it.
        dispatch(propertiesActions.setPropertyFilter(filterSnapshot));
    }

    // One criterion per checked row, so every box narrows the result — including two
    // values of the same property, which asks for objects carrying both.
    const searchCriteria: SearchCriteria[] = Object.values(selectedProperties).map(
        ({ searchPropertyName, propertyValue }) => ({ property: searchPropertyName, value: [propertyValue] }),
    );
    const query: PropertyQuery = {
        terms: searchCriteria.map(({ property, value }) => ({ property, anyOf: value })),
    };

    try {
        await withLoadingState(
            async () => {
                // Paint = matches plus descendants; mainObjects = match IDs only so the
                // tree does not re-check whole subtrees (BM-13021 / #1637).
                const [paintedIds, matchIds] = await Promise.all([
                    findSimilarObjects({ dataAccess, searchCriteria, signal }),
                    dataAccess.findIdsByProperties(query, { signal }),
                ]);

                if (signal?.aborted) {
                    return;
                }

                // A later toggle, or leaving X-ray by another route, may have replaced this
                // filter while the search was in flight. Applying a stale result now would
                // resurrect a filter the user already changed or left — cancellation only
                // catches a newer toggle, not an exit through an unrelated action, so this
                // stays as the backstop for every route.
                if (selectSelectedProperties(store.getState()) !== selectedProperties) {
                    return;
                }

                const apply = (paint: Iterable<ObjectId>, matches: Iterable<ObjectId>) => {
                    selectObjectsRaw(paint, { dispatchHighlighted });
                    applyXRayIsolation({ dispatch, currentDefaultVisibility });
                    dispatch(propertiesActions.setPropertyFilter(filterSnapshot));
                    dispatch(renderActions.setMainObjects(Array.from(matches)));
                };
                apply(paintedIds, matchIds);

                // The search runs once; redo replays its result rather than re-querying.
                // Shrink paint and matches separately so redo cannot dump descendants into mainObjects.
                const shrunkPaint = snapshotUndoIds(paintedIds);
                const shrunkMatches = snapshotUndoIds(matchIds);
                dispatch(
                    undoRedoActions.push({
                        description: "Filter by properties",
                        undoCallback: undo,
                        redoCallback: () => apply(restoreUndoIds(shrunkPaint), restoreUndoIds(shrunkMatches).sortedIds),
                    }),
                );
            },
            { dispatch },
        );
    } catch (error) {
        // Cancellation surfaces as a rejection on some backends, not just a resolved,
        // ignored result — either way it's not a real failure.
        if (signal?.aborted) {
            return;
        }
        console.error("Error in isolateSimilarInXray:", error);
        telemetry.trackCaughtException({
            source: "utils.objectActions.isolateSimilarInXray",
            category: "data",
            error,
            severity: "error",
        });
    }
}

/**
 * Select objects in a normal tree (non-filtered).
 * For folders, selects all descendants by querying the database.
 * Includes the folder ID itself in the selection set.
 *
 * @param dispatchHidden - Optional. When provided, selected objects will be automatically
 *                         unhidden. In the Object Tree window, this is intentionally NOT passed
 *                         to allow objects to exist in both selected and hidden sets simultaneously.
 *                         Other components (like Model Tree) may pass this to maintain automatic
 *                         unhiding behavior.
 */
export async function selectObjectHierarchy({
    nodes,
    dataAccess,
    abortSignal,
    setLoading,
    dispatchHighlighted,
    dispatchHidden,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHighlighted: DispatchHighlighted;
    dispatchHidden?: DispatchHidden;
}): Promise<number[]> {
    // Honor an already-aborted signal up front so every path (leaf, cached,
    // async) bails consistently. The meaningful guard is the post-await check
    // below; this just avoids work when the caller hands us a dead signal.
    if (abortSignal.aborted) {
        return [];
    }

    const perf = startMeasure("selectObjectHierarchy");

    const isLeaf = isLeafNode(nodes[0]);
    if (isLeaf) {
        const ids = nodes.map((n) => n.id);
        dispatchHighlighted(highlightActions.add(ids));
        if (dispatchHidden) {
            dispatchHidden(hiddenActions.remove(ids));
        }
        perf.end({ leaf: true, totalIds: ids.length });
        return ids;
    }

    const node = nodes[0];

    setLoading(true);
    try {
        const addedIds = [...(await dataAccess.getDescendantIds(node, abortSignal))];

        perf.mark("fetchComplete", { nodeId: node.id, descendantCount: addedIds.length });

        // Cancelled while fetching: nothing is dispatched, so return [] to avoid
        // callers recording undo/redo for a selection that never applied.
        if (abortSignal.aborted) {
            perf.end({ aborted: true });
            return [];
        }

        // Single dispatch for all IDs (descendants + parent node)
        addedIds.push(node.id);
        dispatchHighlighted(highlightActions.add(addedIds));
        if (dispatchHidden) {
            dispatchHidden(hiddenActions.remove(addedIds));
        }
        perf.end({ totalIds: addedIds.length });
        return addedIds;
    } catch (e) {
        perf.end({ error: true });
        if (!abortSignal.aborted) {
            telemetry.trackCaughtException({
                source: "utils.objectActions.selectObjectHierarchy",
                category: "data",
                error: e,
            });
        }
        // Fetch failed before any dispatch — nothing was applied.
        return [];
    } finally {
        // Always clear loading (even on abort) so the shared loading indicator
        // can't get stuck while the component stays mounted.
        setLoading(false);
    }
}

export async function unselectObjectHierarchy({
    nodes,
    dataAccess,
    abortSignal,
    setLoading,
    dispatchHighlighted,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHighlighted: DispatchHighlighted;
}): Promise<number[]> {
    // Honor an already-aborted signal up front so every path (leaf, cached,
    // async) bails consistently. The meaningful guard is the post-await check
    // below; this just avoids work when the caller hands us a dead signal.
    if (abortSignal.aborted) {
        return [];
    }

    const perf = startMeasure("unselectObjectHierarchy");

    const isLeaf = isLeafNode(nodes[0]);
    const ids = nodes.map((n) => n.id);
    if (isLeaf) {
        dispatchHighlighted(highlightActions.remove(ids));
        perf.end({ leaf: true, totalIds: ids.length });
        return ids;
    }

    const node = nodes[0];

    setLoading(true);

    try {
        const removedIds = [...ids, ...(await dataAccess.getDescendantIds(node, abortSignal))];

        perf.mark("fetchComplete", { nodeId: node.id, totalIds: removedIds.length });

        // Cancelled while fetching: nothing is dispatched, so return [] to avoid
        // callers recording undo/redo for an unselection that never applied.
        if (abortSignal.aborted) {
            perf.end({ aborted: true });
            return [];
        }

        // Single dispatch for all IDs
        dispatchHighlighted(highlightActions.remove(removedIds));
        perf.end({ totalIds: removedIds.length });
        return removedIds;
    } catch (e) {
        perf.end({ error: true });
        if (!abortSignal.aborted) {
            telemetry.trackCaughtException({
                source: "utils.objectActions.unselectObjectHierarchy",
                category: "data",
                error: e,
            });
        }
        // Fetch failed before any dispatch — nothing was applied.
        return [];
    } finally {
        // Always clear loading (even on abort) so the shared loading indicator
        // can't get stuck while the component stays mounted.
        setLoading(false);
    }
}

/**
 * Hide objects in a normal tree.
 * For folders, hides all descendants by querying the database.
 * Includes the folder ID itself in the hidden set.
 */
export async function hideObjectHierarchy({
    nodes,
    dataAccess,
    abortSignal,
    setLoading,
    dispatchHidden,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHidden: DispatchHidden;
}): Promise<number[]> {
    const isLeaf = isLeafNode(nodes[0]);
    if (isLeaf) {
        const ids = nodes.map((n) => n.id);
        dispatchHidden(hiddenActions.add(ids));
        dispatchHidden(hiddenActions.setLastHiddenIds(ids));
        return ids;
    }

    setLoading(true);
    const node = nodes[0];
    let addedIds: number[] = [];
    try {
        const ids = [...(await dataAccess.getDescendantIds(node, abortSignal)), node.id];

        // Cancelled while fetching: nothing is dispatched, so leave `addedIds` empty to
        // avoid callers recording undo/redo for a hide that never applied.
        if (!abortSignal.aborted) {
            addedIds = ids;
            dispatchHidden(hiddenActions.add(addedIds));
            dispatchHidden(hiddenActions.setLastHiddenIds(addedIds));
        }
    } catch (e) {
        if (!abortSignal.aborted) {
            telemetry.trackCaughtException({
                source: "utils.objectActions.hideObjectHierarchy",
                category: "data",
                error: e,
            });
        }
    } finally {
        // Always clear loading (even on abort) so the shared loading indicator
        // can't get stuck while the component stays mounted.
        setLoading(false);
    }
    return addedIds;
}

/**
 * Show (unhide) objects in a normal tree.
 * For folders, shows all descendants by querying the database.
 * Removes both the folder ID and all descendant IDs from the hidden set.
 */
export async function showObjectHierarchy({
    nodes,
    dataAccess,
    abortSignal,
    setLoading,
    dispatchHidden,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHidden: DispatchHidden;
}): Promise<number[]> {
    const isLeaf = isLeafNode(nodes[0]);
    const ids = nodes.map((n) => n.id);
    if (isLeaf) {
        dispatchHidden(hiddenActions.remove(ids));
        return ids;
    }

    dispatchHidden(hiddenActions.remove(ids));
    let removedIds = ids;
    setLoading(true);

    const node = nodes[0];
    try {
        const descendantIds = await dataAccess.getDescendantIds(node, abortSignal);

        // An aborted read answers empty, and the object-DB fallback can answer *partially*
        // — applying either would unhide an arbitrary slice of the subtree.
        if (!abortSignal.aborted) {
            removedIds = [...removedIds, ...descendantIds];
            dispatchHidden(hiddenActions.remove(descendantIds));
        }
    } catch (e) {
        if (!abortSignal.aborted) {
            telemetry.trackCaughtException({
                source: "utils.objectActions.showObjectHierarchy",
                category: "data",
                error: e,
            });
        }
    } finally {
        // Always clear loading (even on abort) so the shared loading indicator
        // can't get stuck while the component stays mounted.
        setLoading(false);
    }
    return removedIds;
}

/**
 * Change the captured keep-set while plain isolation is active. Hiding removes
 * IDs only from the keep-set, so Unisolate restores them. Showing adds IDs to
 * the keep-set and also clears any explicit user-hidden state for those IDs.
 */
export async function setObjectHierarchyIsolationVisibility({
    nodes,
    visible,
    dataAccess,
    abortSignal,
    setLoading,
    dispatchHidden,
    dispatchHighlightCollection,
    filteredObjectIds,
}: {
    nodes: HierarcicalObjectReference[];
    visible: boolean;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHidden: DispatchHidden;
    dispatchHighlightCollection: DispatchHighlightCollection;
    filteredObjectIds?: Set<number> | null;
}): Promise<number[]> {
    if (abortSignal.aborted || nodes.length === 0) {
        return [];
    }

    let ids: number[];
    if (filteredObjectIds) {
        const resolved = await resolveFilteredHierarchyIds({
            nodes,
            filteredObjectIds,
            dataAccess,
            abortSignal,
            setLoading,
        });
        if (resolved === null) {
            return [];
        }
        ids = resolved;
    } else {
        const isLeaf = isLeafNode(nodes[0]);
        ids = nodes.map((node) => node.id);
        if (!isLeaf) {
            setLoading(true);
            try {
                ids = [...ids, ...(await dataAccess.getDescendantIds(nodes[0], abortSignal))];
            } catch (error) {
                if (!abortSignal.aborted) {
                    telemetry.trackCaughtException({
                        source: "utils.objectActions.setObjectHierarchyIsolationVisibility",
                        category: "data",
                        error,
                    });
                }
                return [];
            } finally {
                setLoading(false);
            }
        }
    }

    if (abortSignal.aborted) {
        return [];
    }

    dispatchHighlightCollection(
        visible
            ? highlightCollectionsActions.add(HighlightCollection.TransparentSelectionBase, ids)
            : highlightCollectionsActions.remove(HighlightCollection.TransparentSelectionBase, ids),
    );
    if (visible) {
        dispatchHidden(hiddenActions.remove(ids));
    }
    return ids;
}

interface FilteredHierarchyVisibilityParams {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dispatchHidden: DispatchHidden;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
}

/**
 * Resolves the full set of object IDs a visibility toggle should affect for a row
 * of the filtered (search-result) tree.
 *
 * Rows behave like the unfiltered tree: toggling a non-leaf row (a matched folder
 * or a synthetic ancestor of a match) affects its entire real hierarchy. The
 * filtered tree only contains the matched objects plus synthetic ancestor
 * placeholders — never the real descendants — so the subtree is fetched from the
 * database. Synthetic ancestors have no real object id, so their subtree is
 * resolved by path instead of via the id-based descendants endpoint.
 *
 * Returns null when the operation was aborted mid-fetch (nothing should be dispatched).
 */
async function resolveFilteredHierarchyIds({
    nodes,
    filteredObjectIds,
    dataAccess,
    abortSignal,
    setLoading,
}: Omit<FilteredHierarchyVisibilityParams, "dispatchHidden">): Promise<number[] | null> {
    const node = nodes[0];

    if (isLeafNode(node)) {
        // Leaf rows in the filtered tree are search matches; toggle them directly.
        return nodes.filter((n) => filteredObjectIds.has(n.id)).map((n) => n.id);
    }

    // The synthetic id must never enter the hidden set — it is shared by every
    // synthetic ancestor node and has no renderable geometry.
    const ids = new Set<number>();
    if (node.id !== SYNTHETIC_NODE_ID) {
        ids.add(node.id);
    }

    setLoading(true);
    try {
        // A synthetic row stands for a real path with no object of its own to ask by, so
        // the object there is resolved first and its subtree read from that. Its own id is
        // deliberately still left out of `ids` — only the descendants are toggled, as
        // before.
        const collectDescendantsByPath = async () => {
            const real = await dataAccess.getObjectAtPath(node.path, abortSignal);
            if (!real) {
                return;
            }
            for (const id of await dataAccess.getDescendantIds(real, abortSignal)) {
                ids.add(id);
            }
        };

        if (node.id === SYNTHETIC_NODE_ID) {
            if (node.descendants) {
                for (const id of node.descendants) {
                    ids.add(id);
                }
            } else {
                await collectDescendantsByPath();
                if (!abortSignal.aborted) {
                    // Cache the resolved subtree on the synthetic node (mirroring what
                    // getDescendants does for real nodes). Besides skipping the search on
                    // the next toggle, this lets the tree row derive its eye-icon state
                    // from the subtree's hidden coverage — the fake id itself never
                    // enters the hidden set. `ids` holds only descendants here since the
                    // synthetic id is never added.
                    (node as DeepMutable<HierarcicalObjectReference>).descendants = Array.from(ids);
                }
            }
        } else {
            for (const id of await dataAccess.getDescendantIds(node, abortSignal)) {
                ids.add(id);
            }
        }
    } finally {
        setLoading(false);
    }

    if (abortSignal.aborted) {
        return null;
    }

    return Array.from(ids);
}

/**
 * Hide objects from a filtered tree (e.g., search results).
 * Non-leaf rows (matched folders and synthetic ancestors of matches) hide their entire
 * real hierarchy, fetched from the database, so the geometry is actually hidden in 3D —
 * matching the behavior of the unfiltered tree.
 */
export async function hideObjectHierarchyFromFilteredTree(
    params: FilteredHierarchyVisibilityParams,
): Promise<number[]> {
    const { dispatchHidden } = params;

    const idsToHide = await resolveFilteredHierarchyIds(params);
    if (idsToHide === null) {
        // Aborted mid-fetch: nothing is dispatched, so return [] to avoid
        // callers recording undo/redo for a hide that never applied.
        return [];
    }

    dispatchHidden(hiddenActions.add(idsToHide));
    dispatchHidden(hiddenActions.setLastHiddenIds(idsToHide));
    return idsToHide;
}

/**
 * Show (unhide) objects from a filtered tree (e.g., search results).
 * Non-leaf rows (matched folders and synthetic ancestors of matches) show their entire
 * real hierarchy, fetched from the database, so the geometry is actually shown in 3D —
 * matching the behavior of the unfiltered tree.
 */
export async function showObjectHierarchyFromFilteredTree(
    params: FilteredHierarchyVisibilityParams,
): Promise<number[]> {
    const { dispatchHidden } = params;

    const idsToShow = await resolveFilteredHierarchyIds(params);
    if (idsToShow === null) {
        return [];
    }

    dispatchHidden(hiddenActions.remove(idsToShow));
    return idsToShow;
}

interface FilteredHierarchySelectionParams {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dispatchHighlighted: DispatchHighlighted;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
}

/**
 * Select objects from a filtered tree (e.g., search results).
 * Non-leaf rows (matched folders and synthetic ancestors of matches) select their entire
 * real hierarchy, fetched from the database via {@link resolveFilteredHierarchyIds} — the
 * same resolution used by hideObjectHierarchyFromFilteredTree/showObjectHierarchyFromFilteredTree.
 *
 * This matters because that hierarchy can include real descendants that did NOT match the
 * search themselves (e.g. objects with no geometry). Using the plain (non-filtered-tree-aware)
 * selectObjectHierarchy here would try to resolve a synthetic ancestor row (fake id -1) via the
 * id-based descendants endpoint, which silently resolves to an empty list and caches that empty
 * result on the shared node — leaving the row stuck showing "partially selected" and breaking
 * the eye icon's isolate/unisolate for that row afterwards, since it would reuse the same
 * (incorrectly empty) cached subtree.
 */
export async function selectObjectHierarchyFromFilteredTree({
    nodes,
    filteredObjectIds,
    dispatchHighlighted,
    dataAccess,
    abortSignal,
    setLoading,
}: FilteredHierarchySelectionParams): Promise<number[]> {
    const idsToSelect = await resolveFilteredHierarchyIds({
        nodes,
        filteredObjectIds,
        dataAccess,
        abortSignal,
        setLoading,
    });
    if (idsToSelect === null) {
        // Aborted mid-fetch: nothing is dispatched, so return [] to avoid
        // callers recording undo/redo for a selection that never applied.
        return [];
    }

    dispatchHighlighted(highlightActions.add(idsToSelect));
    return idsToSelect;
}

/**
 * Unselect objects from a filtered tree (e.g., search results). See
 * {@link selectObjectHierarchyFromFilteredTree} for why this resolves the row's real hierarchy
 * the same way the visibility toggle does, instead of using the plain unselectObjectHierarchy.
 */
export async function unselectObjectHierarchyFromFilteredTree({
    nodes,
    filteredObjectIds,
    dispatchHighlighted,
    dataAccess,
    abortSignal,
    setLoading,
}: FilteredHierarchySelectionParams): Promise<number[]> {
    const idsToUnselect = await resolveFilteredHierarchyIds({
        nodes,
        filteredObjectIds,
        dataAccess,
        abortSignal,
        setLoading,
    });
    if (idsToUnselect === null) {
        return [];
    }

    dispatchHighlighted(highlightActions.remove(idsToUnselect));
    return idsToUnselect;
}

/**
 * Fetches all descendant IDs for a list of nodes in parallel with a concurrency limit.
 * This is the shared core for bulk select/unselect operations — it performs only data
 * fetching with no state side effects.
 *
 * @returns Array of all collected object IDs (node IDs + their descendants)
 */
async function fetchAllDescendantIds({
    nodes,
    dataAccess,
    abortSignal,
    concurrency = 10,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    concurrency?: number;
}): Promise<{ allIds: number[]; descendantFetchCount: number }> {
    // Accumulate into a Set so duplicates are dropped as we go, keeping peak
    // memory lower than building a full array and de-duplicating at the end.
    const allIds = new Set<number>();
    let descendantFetchCount = 0;

    const fetchDescendants = async (node: HierarcicalObjectReference): Promise<number[]> => {
        if (abortSignal.aborted) {
            return [];
        }

        const ids: number[] = [node.id];

        if (isLeafNode(node)) {
            return ids;
        }

        try {
            for (const id of await dataAccess.getDescendantIds(node, abortSignal)) {
                allIds.add(id);
            }
        } catch (error) {
            // One unreadable subtree costs its own ids, not the whole bulk operation.
            if (abortSignal.aborted || (error as { name?: string }).name === "AbortError") {
                return [];
            }
            return [];
        }

        descendantFetchCount++;
        return ids;
    };

    // Run fetches in parallel with a concurrency limit. Note: we intentionally
    // do not pass pMap's `signal` option — callers expect this to resolve with
    // partial results on abort (then check `abortSignal.aborted`), not throw.
    // `fetchDescendants` already short-circuits once the signal is aborted.
    await pMap(
        nodes,
        async (node) => {
            const ids = await fetchDescendants(node);
            for (const id of ids) {
                allIds.add(id);
            }
        },
        { concurrency },
    );

    return { allIds: Array.from(allIds), descendantFetchCount };
}

/**
 * Collects all matching IDs for the filtered tree's "select/unselect all" bulk action.
 *
 * This used to walk the in-memory tree (via each top-level node's `children`) to find
 * every matched descendant. But a matched folder that has no further match beneath it
 * intentionally leaves its own `children` unloaded (see buildFilteredTree) so it can be
 * expanded to its full real hierarchy rather than being capped at other matches — which
 * means such a folder's real (non-matching) descendants are never reachable by that
 * traversal in the first place. `filteredObjectIds` already holds every matched id
 * regardless of what's currently loaded in the tree, so it's used directly instead.
 */
function collectFilteredTreeIds({ filteredObjectIds }: { filteredObjectIds: Set<number> }): number[] {
    return Array.from(filteredObjectIds);
}

/**
 * Optimized bulk selection for "select all" operations.
 * Parallelizes network fetches across multiple root nodes and dispatches
 * a single state update at the end, avoiding costly intermediate re-renders.
 *
 * @returns Array of all selected object IDs
 */
export async function bulkSelectAllHierarchies({
    nodes,
    dataAccess,
    abortSignal,
    dispatchHighlighted,
    concurrency = 10,
    parentMeasure,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    dispatchHighlighted: DispatchHighlighted;
    concurrency?: number;
    parentMeasure?: PerfMeasurement;
}): Promise<number[]> {
    const perf = startMeasure("bulkSelectAllHierarchies", parentMeasure);

    try {
        const { allIds, descendantFetchCount } = await fetchAllDescendantIds({
            nodes,
            dataAccess,
            abortSignal,
            concurrency,
        });

        perf.mark("fetchComplete", { nodeCount: nodes.length, descendantFetchCount, totalIds: allIds.length });

        if (abortSignal.aborted) {
            perf.end({ aborted: true });
            return [];
        }

        dispatchHighlighted(highlightActions.add(allIds));

        perf.end({ totalIds: allIds.length });
        return allIds;
    } catch (error) {
        // Ensure the span is always closed, then rethrow for the caller to handle.
        perf.end({ error: true });
        throw error;
    }
}

/**
 * Optimized bulk unselection for "unselect all" operations.
 * Parallelizes network fetches and dispatches a single state update.
 *
 * @returns Array of all unselected object IDs
 */
export async function bulkUnselectAllHierarchies({
    nodes,
    dataAccess,
    abortSignal,
    dispatchHighlighted,
    concurrency = 10,
    parentMeasure,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    dispatchHighlighted: DispatchHighlighted;
    concurrency?: number;
    parentMeasure?: PerfMeasurement;
}): Promise<number[]> {
    const perf = startMeasure("bulkUnselectAllHierarchies", parentMeasure);

    try {
        const { allIds, descendantFetchCount } = await fetchAllDescendantIds({
            nodes,
            dataAccess,
            abortSignal,
            concurrency,
        });

        perf.mark("fetchComplete", { nodeCount: nodes.length, descendantFetchCount, totalIds: allIds.length });

        if (abortSignal.aborted) {
            perf.end({ aborted: true });
            return [];
        }

        dispatchHighlighted(highlightActions.remove(allIds));

        perf.end({ totalIds: allIds.length });
        return allIds;
    } catch (error) {
        // Ensure the span is always closed, then rethrow for the caller to handle.
        perf.end({ error: true });
        throw error;
    }
}

/**
 * Optimized bulk selection for filtered trees (search results).
 * Since filtered tree operations are synchronous (in-memory traversal),
 * this collects all IDs first and dispatches once.
 *
 * @returns Array of all selected object IDs
 */
export function bulkSelectAllFromFilteredTree({
    filteredObjectIds,
    dispatchHighlighted,
    parentMeasure,
}: {
    filteredObjectIds: Set<number>;
    dispatchHighlighted: DispatchHighlighted;
    parentMeasure?: PerfMeasurement;
}): number[] {
    const perf = startMeasure("bulkSelectAllFromFilteredTree", parentMeasure);

    try {
        const allIds = collectFilteredTreeIds({ filteredObjectIds });

        perf.mark("collected", { totalIds: allIds.length });

        dispatchHighlighted(highlightActions.add(allIds));

        perf.end({ totalIds: allIds.length });
        return allIds;
    } catch (error) {
        // Ensure the span is always closed, then rethrow for the caller to handle.
        perf.end({ error: true });
        throw error;
    }
}

/**
 * Optimized bulk unselection for filtered trees (search results).
 * Collects all IDs first and dispatches a single remove.
 *
 * @returns Array of all unselected object IDs
 */
export function bulkUnselectAllFromFilteredTree({
    filteredObjectIds,
    dispatchHighlighted,
    parentMeasure,
}: {
    filteredObjectIds: Set<number>;
    dispatchHighlighted: DispatchHighlighted;
    parentMeasure?: PerfMeasurement;
}): number[] {
    const perf = startMeasure("bulkUnselectAllFromFilteredTree", parentMeasure);

    try {
        const allIds = collectFilteredTreeIds({ filteredObjectIds });

        perf.mark("collected", { totalIds: allIds.length });

        dispatchHighlighted(highlightActions.remove(allIds));

        perf.end({ totalIds: allIds.length });
        return allIds;
    } catch (error) {
        // Ensure the span is always closed, then rethrow for the caller to handle.
        perf.end({ error: true });
        throw error;
    }
}

/**
 * Resolves the filtered-tree hierarchy IDs for several independent rows (e.g. the
 * top-level nodes toggled by "hide/show all") in parallel with a concurrency limit,
 * mirroring {@link fetchAllDescendantIds}'s "parallel fetch, single dispatch"
 * optimization for the unfiltered tree. Each row is resolved via
 * {@link resolveFilteredHierarchyIds} (which fetches the row's real hierarchy from
 * the database and caches it on synthetic ancestor nodes) — this is purely the
 * fan-out; callers dispatch once with the aggregated result.
 */
async function fetchAllFilteredHierarchyIds({
    nodes,
    filteredObjectIds,
    dataAccess,
    abortSignal,
    concurrency = 10,
}: {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    concurrency?: number;
}): Promise<number[]> {
    const allIds = new Set<number>();

    await pMap(
        nodes,
        async (node) => {
            const ids = await resolveFilteredHierarchyIds({
                nodes: [node],
                filteredObjectIds,
                dataAccess,
                abortSignal,
                setLoading: () => {},
            });
            if (ids) {
                for (const id of ids) {
                    allIds.add(id);
                }
            }
        },
        { concurrency },
    );

    return Array.from(allIds);
}

/**
 * Optimized bulk hide for "hide all" operations on a normal (unfiltered) tree.
 * Parallelizes network fetches across multiple root nodes and dispatches a single
 * state update at the end, mirroring {@link bulkSelectAllHierarchies}.
 *
 * @returns Array of all hidden object IDs
 */
export async function bulkHideAllHierarchies({
    nodes,
    dataAccess,
    abortSignal,
    dispatchHidden,
    concurrency = 10,
    parentMeasure,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    dispatchHidden: DispatchHidden;
    concurrency?: number;
    parentMeasure?: PerfMeasurement;
}): Promise<number[]> {
    const perf = startMeasure("bulkHideAllHierarchies", parentMeasure);

    try {
        const { allIds, descendantFetchCount } = await fetchAllDescendantIds({
            nodes,
            dataAccess,
            abortSignal,
            concurrency,
        });

        perf.mark("fetchComplete", { nodeCount: nodes.length, descendantFetchCount, totalIds: allIds.length });

        if (abortSignal.aborted) {
            perf.end({ aborted: true });
            return [];
        }

        dispatchHidden(hiddenActions.add(allIds));
        dispatchHidden(hiddenActions.setLastHiddenIds(allIds));

        perf.end({ totalIds: allIds.length });
        return allIds;
    } catch (error) {
        // Ensure the span is always closed, then rethrow for the caller to handle.
        perf.end({ error: true });
        throw error;
    }
}

/**
 * Optimized bulk show for "show all" operations on a normal (unfiltered) tree.
 * Parallelizes network fetches and dispatches a single state update.
 *
 * @returns Array of all shown object IDs
 */
export async function bulkShowAllHierarchies({
    nodes,
    dataAccess,
    abortSignal,
    dispatchHidden,
    concurrency = 10,
    parentMeasure,
}: {
    nodes: HierarcicalObjectReference[];
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    dispatchHidden: DispatchHidden;
    concurrency?: number;
    parentMeasure?: PerfMeasurement;
}): Promise<number[]> {
    const perf = startMeasure("bulkShowAllHierarchies", parentMeasure);

    try {
        const { allIds, descendantFetchCount } = await fetchAllDescendantIds({
            nodes,
            dataAccess,
            abortSignal,
            concurrency,
        });

        perf.mark("fetchComplete", { nodeCount: nodes.length, descendantFetchCount, totalIds: allIds.length });

        if (abortSignal.aborted) {
            perf.end({ aborted: true });
            return [];
        }

        dispatchHidden(hiddenActions.remove(allIds));

        perf.end({ totalIds: allIds.length });
        return allIds;
    } catch (error) {
        // Ensure the span is always closed, then rethrow for the caller to handle.
        perf.end({ error: true });
        throw error;
    }
}

/**
 * Optimized bulk hide for "hide all" operations on a filtered tree (search results).
 * Parallelizes the per-row database fetches (see {@link fetchAllFilteredHierarchyIds})
 * and dispatches a single state update, instead of resolving and dispatching one row
 * at a time.
 *
 * @returns Array of all hidden object IDs
 */
export async function bulkHideAllFromFilteredTree({
    nodes,
    filteredObjectIds,
    dispatchHidden,
    dataAccess,
    abortSignal,
    concurrency = 10,
    parentMeasure,
}: {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dispatchHidden: DispatchHidden;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    concurrency?: number;
    parentMeasure?: PerfMeasurement;
}): Promise<number[]> {
    const perf = startMeasure("bulkHideAllFromFilteredTree", parentMeasure);

    try {
        const allIds = await fetchAllFilteredHierarchyIds({
            nodes,
            filteredObjectIds,
            dataAccess,
            abortSignal,
            concurrency,
        });

        perf.mark("fetchComplete", { nodeCount: nodes.length, totalIds: allIds.length });

        if (abortSignal.aborted) {
            perf.end({ aborted: true });
            return [];
        }

        dispatchHidden(hiddenActions.add(allIds));
        dispatchHidden(hiddenActions.setLastHiddenIds(allIds));

        perf.end({ totalIds: allIds.length });
        return allIds;
    } catch (error) {
        // Ensure the span is always closed, then rethrow for the caller to handle.
        perf.end({ error: true });
        throw error;
    }
}

/**
 * Optimized bulk show for "show all" operations on a filtered tree (search results).
 * Parallelizes the per-row database fetches and dispatches a single state update.
 *
 * @returns Array of all shown object IDs
 */
export async function bulkShowAllFromFilteredTree({
    nodes,
    filteredObjectIds,
    dispatchHidden,
    dataAccess,
    abortSignal,
    concurrency = 10,
    parentMeasure,
}: {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dispatchHidden: DispatchHidden;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    concurrency?: number;
    parentMeasure?: PerfMeasurement;
}): Promise<number[]> {
    const perf = startMeasure("bulkShowAllFromFilteredTree", parentMeasure);

    try {
        const allIds = await fetchAllFilteredHierarchyIds({
            nodes,
            filteredObjectIds,
            dataAccess,
            abortSignal,
            concurrency,
        });

        perf.mark("fetchComplete", { nodeCount: nodes.length, totalIds: allIds.length });

        if (abortSignal.aborted) {
            perf.end({ aborted: true });
            return [];
        }

        dispatchHidden(hiddenActions.remove(allIds));

        perf.end({ totalIds: allIds.length });
        return allIds;
    } catch (error) {
        // Ensure the span is always closed, then rethrow for the caller to handle.
        perf.end({ error: true });
        throw error;
    }
}
